/**
 * Utility functions for handling API errors and providing user-friendly error messages
 */

/**
 * Extracts a user-friendly error message from an API error
 * @param error - The error object from the API call
 * @param defaultMessage - Default message to show if no specific error is found
 * @returns User-friendly error message in Thai
 */
export const getApiErrorMessage = (
  error: unknown,
  defaultMessage: string = 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง'
): string => {
  if (error instanceof Error) {
    // Check for specific error types
    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      return 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
    } else if (error.message.includes('400')) {
      return 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลที่กรอกแล้วลองใหม่';
    } else if (error.message.includes('401') || error.message.includes('403')) {
      return 'คุณไม่มีสิทธิ์ในการดำเนินการ กรุณาติดต่อผู้ดูแลระบบ';
    } else if (error.message.includes('409')) {
      return 'ข้อมูลนี้มีอยู่แล้วในระบบ กรุณาใช้ข้อมูลที่มีอยู่';
    } else if (error.message.includes('500')) {
      return 'เกิดข้อผิดพลาดในระบบ กรุณาลองใหม่อีกครั้งหรือติดต่อผู้ดูแลระบบ';
    } else if (error.message) {
      return error.message;
    }
  }
  
  return defaultMessage;
};

/**
 * Specialized error message handler for create operations
 * @param error - The error object from the create API call
 * @param entityType - Type of entity being created (for context)
 * @returns User-friendly error message in Thai
 */
export const getCreateErrorMessage = (
  error: unknown,
  entityType?: string
): string => {
  const defaultMessage = entityType 
    ? `ไม่สามารถสร้าง${entityType}ใหม่ได้ กรุณาลองใหม่อีกครั้ง`
    : 'ไม่สามารถสร้างตัวเลือกใหม่ได้ กรุณาลองใหม่อีกครั้ง';

  if (error instanceof Error) {
    // Check for specific error types with create-specific messages
    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      return 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
    } else if (error.message.includes('400')) {
      return 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลที่กรอกแล้วลองใหม่';
    } else if (error.message.includes('401') || error.message.includes('403')) {
      return 'คุณไม่มีสิทธิ์ในการสร้างตัวเลือกใหม่ กรุณาติดต่อผู้ดูแลระบบ';
    } else if (error.message.includes('409')) {
      return 'ตัวเลือกนี้มีอยู่แล้วในระบบ กรุณาใช้ตัวเลือกที่มีอยู่';
    } else if (error.message.includes('500')) {
      return 'เกิดข้อผิดพลาดในระบบ กรุณาลองใหม่อีกครั้งหรือติดต่อผู้ดูแลระบบ';
    } else if (error.message) {
      return error.message;
    }
  }
  
  return defaultMessage;
};

/**
 * HTTP status code to Thai error message mapping
 */
export const HTTP_ERROR_MESSAGES = {
  400: 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลที่กรอกแล้วลองใหม่',
  401: 'คุณไม่มีสิทธิ์ในการเข้าถึง กรุณาเข้าสู่ระบบใหม่',
  403: 'คุณไม่มีสิทธิ์ในการดำเนินการ กรุณาติดต่อผู้ดูแลระบบ',
  404: 'ไม่พบข้อมูลที่ต้องการ',
  409: 'ข้อมูลนี้มีอยู่แล้วในระบบ',
  422: 'ข้อมูลไม่ถูกต้องตามรูปแบบที่กำหนด',
  429: 'มีการร้องขอมากเกินไป กรุณาลองใหม่อีกครั้งในภายหลัง',
  500: 'เกิดข้อผิดพลาดในระบบ กรุณาลองใหม่อีกครั้งหรือติดต่อผู้ดูแลระบบ',
  502: 'เซิร์ฟเวอร์ไม่สามารถตอบสนองได้ กรุณาลองใหม่อีกครั้ง',
  503: 'ระบบไม่พร้อมให้บริการ กรุณาลองใหม่อีกครั้งในภายหลัง',
} as const;

/**
 * Get error message by HTTP status code
 * @param statusCode - HTTP status code
 * @returns User-friendly error message in Thai
 */
export const getHttpErrorMessage = (statusCode: number): string => {
  return HTTP_ERROR_MESSAGES[statusCode as keyof typeof HTTP_ERROR_MESSAGES] || 
         'เกิดข้อผิดพลาดในการติดต่อกับระบบ กรุณาลองใหม่อีกครั้ง';
};
